'use client';

import { useState, useMemo } from 'react';

import Link from 'next/link';

import CloudinaryImage from '@/components/CloudinaryImage';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Destination } from '@/types';

export default function DestinationsClient({ destinations }: { destinations: Destination[] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const continents = useMemo(() => {
    return Array.from(new Set(destinations.map(d => d.continent).filter(Boolean))).sort();
  }, [destinations]);

  const categories = useMemo(() => {
    return Array.from(new Set(destinations.map(d => d.category || 'Uncategorized'))).sort();
  }, [destinations]);

  const filteredDestinations = useMemo(() => {
    return destinations
      .filter(
        destination =>
          destination.label &&
          destination.label !== 'Unknown' &&
          destination.value &&
          destination.continent &&
          destination.category &&
          destination.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
          (selectedCategory === 'all' || destination.category === selectedCategory)
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [destinations, searchTerm, selectedCategory]);

  return (
    <main>
      <div className="container mx-auto px-4 py-12 sm:py-24 max-w-7xl">
        <h2 className="text-3xl font-bold text-center mb-8">Explore Our Destinations</h2>
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <Input
            type="text"
            placeholder="Search destinations..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className="flex-grow"
          />
          <Select
            onValueChange={(value: string) => setSelectedCategory(value)}
            value={selectedCategory}
          >
            <SelectTrigger className="w-full sm:w-[180px]" aria-label="Select category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category: string) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Tabs defaultValue={continents[0]}>
          <TabsList className="flex flex-wrap justify-center mb-4">
            {continents.map((continent: string) => (
              <TabsTrigger
                key={continent}
                value={continent}
                className="m-1 px-2 py-1 text-xs sm:text-sm"
              >
                {continent}
              </TabsTrigger>
            ))}
          </TabsList>
          {continents.map((continent: string) => (
            <TabsContent key={continent} value={continent}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredDestinations
                  .filter((d: Destination) => d.continent === continent)
                  .map((destination: Destination, index: number) => (
                    <Link
                      href={`/${destination.value}`}
                      key={`${destination.value}-${index}`}
                      className="block"
                      aria-label={`View details for ${destination.label}`}
                    >
                      <div className="bg-background rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:scale-105">
                        <div className="relative h-48">
                          <CloudinaryImage
                            src={`/images/${destination.value.toLowerCase()}`}
                            alt={`Scenic view of ${destination.label}`}
                            width={400}
                            height={192}
                            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                            format="auto"
                            className="object-cover w-full h-full"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-xl font-semibold mb-2">{destination.label}</h3>
                        </div>
                      </div>
                    </Link>
                  ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </main>
  );
}
